import React, { useCallback, useMemo } from 'react';
import { Row, Col, Table } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import TableDeleteButton from './TableDeleteButton';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import DisplayText from '../../../common/text/DisplayText';
import { generateId, getHasColumnPermissions } from '../../formHelpers';
import useCheckTableMaxRows from '../../../common/hooks/useCheckTableMaxRows';
import { calculationTableRender, LINE_NUMBER_COLUMN } from './formFieldsHelpers';
import { getIdMap } from '../../../helpers/helpers';
import Permissions from '../../../auth/Permissions';
import { pendingTableColumn } from '../../forms.constants';

export default function CustomTablePreview({
  title = null,
  columns,
  isDisplay,
  previewProps = {},
  setPreviewProps,
  id,
  setResponses,
  responses = {},
  responding = false,
  configProps = {},
  showCondensedView,
}) {
  const {
    requiredColumns,
    preventEdits,
    columnPermissions = {},
  } = configProps ?? {};
  const {
    values = [], // For Responses
    selected: previewSelected = [],
  } = previewProps;

  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);

  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const {
    [id]: {
      values: responseSelected = [{}],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;

  const updateResponses = useCallback((newData = {}) => {
    setResponses({
      ...responses,
      [id]: {
        ...(responses[id]),
        ...newData,
      },
    });
  }, [responses, id, setResponses]);

  const onDelete = useCallback((deleteIndex) => () => {
    const newSelected = [...selected];
    newSelected.splice(deleteIndex, 1);

    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [previewProps, selected, updateResponses, setPreviewProps]);

  const onDataChanged = useCallback(({ col, index, value }) => {
    const updatedSelected = [...selected];

    const newRow = {
      ...updatedSelected[index],
      [col.name]: value,
    };

    if (!newRow.id) {
      newRow.id = generateId();
    }

    updatedSelected[index] = newRow;

    if (responding) {
      updateResponses({
        values: updatedSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: updatedSelected,
      });
    }
  }, [updateResponses, setPreviewProps, previewProps, selected, responses]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);


  const hasPendingTableData = useMemo(() => dataSource.some((eq) => eq.pendingTableValue), [dataSource]);

  const tableColumns = useMemo(() => {
    const filteredColumns = columns.filter((col) => (
      getHasColumnPermissions({
        column: col,
        columnPermissions,
        userPositionId,
        userId: Permissions.id,
      })
    ));
    const cols = [LINE_NUMBER_COLUMN];

    if (hasPendingTableData) {
      cols.push(pendingTableColumn);
    }

    filteredColumns.forEach((col) => {
      cols.push({
        title: <div className={requiredColumns && 'form-required-field'}>{col.name}</div>,
        dataIndex: col.name,
        render: (text, record, index) => {
          if (col.isCalculation) {
            return calculationTableRender(col, isDisplay)(text);
          }
          return isDisplay || preventEdits ? text : (
            <OnTraccrTextInput
              textarea
              style={{
                minWidth: '10em',
              }}
              onChange={({
                target: {
                  value,
                } = {},
              }) => onDataChanged({ col, index, value })}
              value={record[col.name]}
            />
          );
        },
      });
    });
    if (!isDisplay) {
      cols.push({
        title: '',
        dataIndex: '',
        width: 100,
        render: (_1, _2, index) => (
          <TableDeleteButton onConfirm={onDelete(index)} />
        ),
      });
    }
    return cols;
  }, [
    isDisplay,
    columns,
    onDataChanged,
    onDelete,
    requiredColumns,
    preventEdits,
    columnPermissions,
    userPositionId,
    hasPendingTableData,
  ]);

  const lastRowEmpty = selected.length
    ? Object.values(selected[selected.length - 1]).every((val) => val === '')
    : false;

  const onAddNewClicked = useCallback(() => {
    const newEntry = columns.reduce((result, item) => {
      // eslint-disable-next-line no-param-reassign
      result[item.name] = '';
      return result;
    }, {});

    newEntry.id = generateId();

    const newSelected = selected.concat([newEntry]);
    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [selected, previewProps, responding, columns, setResponses]);

  const {
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  return (
    <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
        <Col>
          {title}
        </Col>
        {!isDisplay && (
          <OnTraccrButton
            type="primary"
            title="Add Row"
            icon={<PlusOutlined />}
            disabled={lastRowEmpty || !shouldAddButtonBeEnabled}
            onClick={onAddNewClicked}
          />
        )}
      </Row>
      { !showCondensedView || dataSource?.length ? (
        <Table
          className='form-table-preview'
          style={{
            width: '100%',
            overflow: 'auto',
          }}
          columns={tableColumns}
          size="small"
          pagination={false}
          dataSource={dataSource}
          rowClassName={(record) => record.pendingTableValue ? 'pending-table-value' : ''}
        />
      ) : (
        <DisplayText title="No Items Selected" style={{ marginBottom: 0 }} />
      )}
    </Row>
  );
}

CustomTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
  })),
  isDisplay: PropTypes.bool,
  previewProps: PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  id: PropTypes.string,
  setResponses: PropTypes.func,
  responses: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      values: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  responding: PropTypes.bool,
  setPreviewProps: PropTypes.func,
  configProps: PropTypes.shape({
    requiredColumns: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
};

CustomTablePreview.defaultProps = {
  title: null,
  columns: [],
  isDisplay: false,
  previewProps: {},
  id: null,
  setResponses: null,
  responses: {},
  responding: false,
  setPreviewProps: null,
  configProps: {},
  showCondensedView: false,
};
